import * as React from 'react'
import { useEffect, useMemo } from 'react'
import { SideDrawerForm } from '@sales-center/components'
import { OwnerSelect } from '@user/components/Owner'
import {
  useActions,
  useData,
} from '@user/components/SalesTeam/ReAssignSalesTeam/hooks'
import { useAppDispatch } from '@user/store/hooks'
import { styles } from '@user/components/SalesTeam/ReAssignSalesTeam/useStyles'
import { ENTITY_TYPES, updateLoadingState } from '@sales-center/common'
import { ISalesTeam, ISalesTeamReassign } from '@user/store/salesTeam'
import MenuItem from '@mui/material/MenuItem'
import { ReAssignSalesTeamAlert } from './ReAssignSalesTeamAlert'
import { Select } from '@workwave-tidal/core/components'
import Stack from '@mui/material/Stack'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import { useTidalThemeActive } from '@user/features/TidalThemeToggle'
import FormHelperText from '@mui/material/FormHelperText'

interface Props {
  onClose: () => void
  onUpdate: (s: number, o: string | null, r: boolean) => void
  salesTeam: ISalesTeamReassign
  entityType: ENTITY_TYPES
  entityId: number
  showInactiveUsers?: boolean
}

export function ReAssignSalesTeamFormContent(props: Props) {
  const {
    onClose,
    entityType,
    entityId,
    salesTeam: { branch },
    onUpdate,
    showInactiveUsers,
  } = props

  const tidalThemeActive = useTidalThemeActive()

  const {
    positionBasedAccessText,
    selectedSalesTeamAccessText,
    teamStatus,
    loadSalesTeamUsers,
    crossBranchValidation,
    crossBranchValidationStatus,
    teamId,
    salesTeamsForUser,
    salesTeamsByBranch,
    isOpportunity,
  } = useData({ salesTeam: props.salesTeam, entityType, entityId })

  const {
    onSave,
    onTeamChange,
    disableSave,
    onRemoveServicesChange,
    removeServices,
  } = useActions({
    onUpdate: onUpdate,
  })

  const dispatch = useAppDispatch()

  useEffect(() => {
    const status =
      teamStatus == 'pending' || crossBranchValidationStatus == 'pending'
    dispatch(updateLoadingState(status ? 'visible' : 'hidden'))
  }, [dispatch, teamStatus, crossBranchValidationStatus])

  useEffect(() => {
    if (teamId > 0) {
      loadSalesTeamUsers(teamId)
    }
  }, [loadSalesTeamUsers, teamId])

  const isSalesTeamRestricted =
    isOpportunity ||
    (crossBranchValidation &&
      (crossBranchValidation.hasServices || crossBranchValidation.isMatched))

  const salesTeams =
    isSalesTeamRestricted && !removeServices
      ? salesTeamsByBranch
      : salesTeamsForUser

  const height = tidalThemeActive ? '65px' : '54px'

  const currentValue = useMemo(
    () => salesTeams?.find((row) => row?.id == teamId),
    [salesTeams, teamId],
  )

  return (
    <SideDrawerForm
      onSave={onSave}
      onCancel={onClose}
      disableSave={disableSave}
      saveBtnDataTestId="reassignSalesTeamFormSave"
      cancelBtnDataTestId="reassignSalesTeamFormCancel"
      tidalThemeActive={tidalThemeActive}
    >
      <Box sx={styles.formSection}>
        <Box component="span" sx={styles.formLabel}>
          Select Sales Team{' '}
        </Box>

        {isSalesTeamRestricted && (
          <ReAssignSalesTeamAlert
            crossBranchValidation={crossBranchValidation}
            entityType={entityType}
            branch={branch}
            onChange={onRemoveServicesChange}
          />
        )}
        {salesTeams && salesTeams.length > 0 && (
          <Box sx={{ mt: 1 }}>
            <Select<ISalesTeam | undefined>
              items={salesTeams}
              onChange={onTeamChange}
              value={currentValue || null}
              required
              getLabel={(option) => `${option?.name} - ${option?.branchName}`}
              label={'Sales Team'}
              testId="reassignSalesTeamFormSalesTeam"
              data-test-id="reassignSalesTeamFormSalesTeam"
              sx={{ height: 6 }}
              listMode="normal"
              optionMode={'custom'}
              renderOption={(props, team: ISalesTeam) => (
                <MenuItem
                  sx={{
                    height: height,
                    minHeight: height,
                  }}
                  {...props}
                  key={team.id}
                  value={team.id}
                >
                  <Stack>
                    <Typography variant="body1">{team?.name}</Typography>
                    <Typography variant="body2">{team?.branchName}</Typography>
                  </Stack>
                </MenuItem>
              )}
            />
            {currentValue == null && !selectedSalesTeamAccessText && (
              <FormHelperText sx={{ ml: 1 }}>
                Select a Sales Team to see the list of Owners.
              </FormHelperText>
            )}
            {selectedSalesTeamAccessText && (
              <FormHelperText sx={{ ml: 1 }} error>
                {selectedSalesTeamAccessText}
              </FormHelperText>
            )}
          </Box>
        )}
      </Box>
      <Box sx={styles.formSection}>
        <Box component="span" sx={styles.formLabel}>
          Select Owner
        </Box>
        <OwnerSelect
          entityType={entityType}
          salesTeamId={`${teamId}`}
          name="ownerId"
          label="Owner"
          size="small"
          errorText={positionBasedAccessText}
          testId="reassignSalesTeamFormOwner"
          required
          useAdditionalMargin
          unassignedOption
          showInactiveUsers={showInactiveUsers}
          ignoreDefault={true}
        />
      </Box>
    </SideDrawerForm>
  )
}
